import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
  Stack,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Publish as PublishIcon,
  Preview as PreviewIcon,
  FileCopy as CopyIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

function DraftForms({ darkMode }) {
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);

  useEffect(() => {
    loadDraftForms();
  }, []);

  const loadDraftForms = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/v1/forms/designs?status=draft', {
        method: 'GET',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('tkn')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setForms(data.forms || []);
      } else {
        setError('Failed to load draft forms');
      }
    } catch (error) {
      console.error('Error loading forms:', error);
      setError('Error loading forms');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, form) => {
    setAnchorEl(event.currentTarget);
    setSelectedForm(form);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedForm(null);
  };

  const handleEditForm = (form) => {
    navigate(`/formBuilder?load=${form._id}`);
    handleMenuClose();
  };

  const handlePreviewForm = (form) => {
    navigate(`/form-runtime?formId=${form._id}`);
    handleMenuClose();
  };

  const handlePublishForm = async (form) => {
    try {
      const response = await fetch(`/api/v1/forms/${form._id}/publish`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('tkn')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        loadDraftForms(); // Refresh the list
      } else {
        setError('Failed to publish form');
      }
    } catch (error) {
      console.error('Error publishing form:', error);
      setError('Error publishing form');
    }
    handleMenuClose();
  };

  const handleDeleteForm = async (form) => {
    if (window.confirm('Are you sure you want to delete this form?')) {
      try {
        const response = await fetch(`/api/v1/forms/design/${form._id}`, {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('tkn')}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          loadDraftForms(); // Refresh the list
        } else {
          setError('Failed to delete form');
        }
      } catch (error) {
        console.error('Error deleting form:', error);
        setError('Error deleting form');
      }
    }
    handleMenuClose();
  };

  const handleDuplicateForm = (form) => {
    navigate(`/formBuilder?duplicate=${form._id}`);
    handleMenuClose();
  };

  const filteredForms = forms.filter((form) => form.name.toLowerCase().includes(searchTerm.toLowerCase())
    || (form.description && form.description.toLowerCase().includes(searchTerm.toLowerCase())));

  if (loading) {
    return (
      <Box sx={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh',
      }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{
      p: 3,
      height: '100%',
      overflow: 'auto',
      display: 'flex',
      flexDirection: 'column',
    }}
    >
      <Box sx={{
        mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}
      >
        <Typography variant="h4" component="h1">
          Draft Forms
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/formBuilder')}
          sx={{ ml: 2 }}
        >
          Create New Form
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search draft forms..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 400 }}
        />
      </Box>

      {filteredForms.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {searchTerm ? 'No draft forms found matching your search' : 'No draft forms found'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {!searchTerm && 'Start building your first form'}
          </Typography>
          {!searchTerm && (
            <Button
              variant="contained"
              onClick={() => navigate('/formBuilder')}
              sx={{ mt: 2 }}
            >
              Create Form
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredForms.map((form) => (
            <Grid item xs={12} sm={6} md={4} key={form._id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1,
                  }}
                  >
                    <Typography variant="h6" component="h2" sx={{ flexGrow: 1, mr: 1 }}>
                      {form.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, form)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>

                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Chip
                      label="Draft"
                      color="warning"
                      size="small"
                    />
                    {form.category && (
                      <Chip
                        label={form.category}
                        variant="outlined"
                        size="small"
                      />
                    )}
                  </Stack>

                  {form.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {form.description}
                    </Typography>
                  )}

                  <Divider sx={{ my: 1 }} />

                  <Typography variant="caption" color="text.secondary">
                    Created:
                    {' '}
                    {new Date(form.createdAt).toLocaleDateString()}
                  </Typography>
                  {form.updatedAt && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Modified:
                      {' '}
                      {new Date(form.updatedAt).toLocaleDateString()}
                    </Typography>
                  )}
                  {form.formElements && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Fields:
                      {' '}
                      {form.formElements.length}
                    </Typography>
                  )}
                </CardContent>

                <CardActions>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditForm(form)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    startIcon={<PreviewIcon />}
                    onClick={() => handlePreviewForm(form)}
                  >
                    Preview
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleEditForm(selectedForm)}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit Form
        </MenuItem>
        <MenuItem onClick={() => handlePreviewForm(selectedForm)}>
          <PreviewIcon sx={{ mr: 1 }} fontSize="small" />
          Preview Form
        </MenuItem>
        <MenuItem onClick={() => handlePublishForm(selectedForm)}>
          <PublishIcon sx={{ mr: 1 }} fontSize="small" />
          Publish Form
        </MenuItem>
        <MenuItem onClick={() => handleDuplicateForm(selectedForm)}>
          <CopyIcon sx={{ mr: 1 }} fontSize="small" />
          Duplicate Form
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleDeleteForm(selectedForm)} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete Form
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default DraftForms;
